import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// Individual row skeleton for job table
export function JobRowSkeleton() {
  return (
    <TableRow className="border-b border-gray-100">
      <TableCell className="p-6">
        <Skeleton className="h-4 w-4" />
      </TableCell>
      <TableCell className="px-6 py-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-12" />
          </div>
        </div>
      </TableCell>
      <TableCell className="py-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-5 w-24" />
        </div>
      </TableCell>
      <TableCell className="py-4">
        <Skeleton className="h-5 w-16" />
      </TableCell>
      <TableCell className="py-4">
        <Skeleton className="h-5 w-14" />
      </TableCell>
      <TableCell className="py-4">
        <Skeleton className="h-4 w-8" />
      </TableCell>
      <TableCell className="py-4">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="py-4">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </TableCell>
    </TableRow>
  );
}

// KPI card skeleton
export function KPICardSkeleton() {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <Skeleton className="h-8 w-12 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-10 w-10 rounded-lg" />
        </div>
      </CardContent>
    </Card>
  );
}

// Complete Job Hub skeleton
export function JobHubSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4 lg:gap-0">
        <div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>

      {/* KPI cards skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <KPICardSkeleton />
        <KPICardSkeleton />
        <KPICardSkeleton />
        <KPICardSkeleton />
      </div>

      {/* Filters skeleton */}
      <Card className="border-0 shadow-sm mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 lg:flex-1">
              <Skeleton className="h-10 w-full lg:max-w-md" />
              <Skeleton className="h-10 w-full sm:w-[180px]" />
              <Skeleton className="h-10 w-full sm:w-[180px]" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table skeleton */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200">
                  <TableHead className="w-12 p-6">
                    <Skeleton className="h-4 w-4" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[250px]">
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[150px]">
                    <Skeleton className="h-4 w-16" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[100px]">
                    <Skeleton className="h-4 w-12" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[100px]">
                    <Skeleton className="h-4 w-14" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[100px]">
                    <Skeleton className="h-4 w-20" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[140px]">
                    <Skeleton className="h-4 w-28" />
                  </TableHead>
                  <TableHead className="py-3 min-w-[100px]">
                    <Skeleton className="h-4 w-16" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 8 }).map((_, index) => (
                  <JobRowSkeleton key={index} />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Generic loading skeleton for smaller components
export function LoadingSkeleton({ 
  rows = 3, 
  className = "" 
}: { 
  rows?: number; 
  className?: string; 
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ))}
    </div>
  );
}
